<style scoped>
    @import '../assets/css/common.css';
    @import '../assets/css/smtpConfiguration.css';
</style>
<template>
    <div class="page">
        <h1>Mail Notifications <span v-show="isDisabled">(Disabled)</span></h3></h1>
        <form @submit="saveData" method="post">
            <notification v-show="isDisabled" class="warning" icon-name="exclamation-triangle">
                <p> You will not receive email notifications if this configuration is disabled.</p>
            </notification>
            <notification v-if="viewNotification && viewNotification.kind !== 'NONE'" :kind="viewNotification.kind">
                <p><b>{{viewNotification.text}}</b></p>
                <p class="error-text" v-show="viewNotification.error">{{viewNotification.error}}</p>
            </notification>
            <fieldset v-show="isConfigurationFetched">
                <p>
                    Use this form to setup SMTP mail server. This server will be used to send notifications about errors and warnings encountered in the Synchronizer.<br/>
                    <a href="https://apps.appf.re/tfs4/doc/dc/mail-notifications" target="_blank">Learn more about Mail Notifications</a>.
                </p>
                <div class="flex-row">
                    <label class="flex-label" for="from-address">From address <span class="red-star">*</span></label>
                    <div class="flex-input">
                        <input type="text" id="from-address" v-model="configuration.FromEmailAddress">
                        <p class="input-description">The default address this server will use to send emails from.</p>
                        <p class="form-error" v-show="formErrors.FromEmailAddress">{{formErrors.FromEmailAddress}}</p>
                    </div>
                </div>
                <div class="flex-row">
                    <label class="flex-label" for="from-address">To address <span class="red-star">*</span></label>
                    <div class="flex-input">
                        <input type="text" id="to-address" v-model="configuration.ToEmailAddress">
                        <p class="input-description">The default address this server will use to send emails to.</p>
                        <p class="form-error" v-show="formErrors.ToEmailAddress">{{formErrors.ToEmailAddress}}</p>
                    </div>
                </div>
                <div class="flex-row">
                    <label class="flex-label" for="email-prefix">Email subject prefix</label>
                    <div class="flex-input">
                        <input type="text" id="email-prefix" v-model="configuration.EmailSubjectPrefix">
                        <p class="input-description">Optional. This prefix will be prepended to all outgoing email subjects.</p>
                    </div>
                </div>
                <div class="flex-row">
                    <label class="flex-label" for="synchronizer-url">This Synchronizer's URL</label>
                    <div class="flex-input">
                        <input type="text" id="synchronizer-url" v-model="configuration.SynchronizerUrl">
                        <p class="input-description">Optional. This URL will be included in email footer.</p>
                    </div>
                </div>
            </fieldset>
            <fieldset v-show="isConfigurationFetched">
                <legend><h4>SMTP Server</h4></legend>
                <div class="flex-row">
                    <label class="flex-label" for="host-name">SMTP host <span class="red-star">*</span></label>
                    <div class="flex-input">
                        <input type="text" id="host-name" v-model="configuration.HostName">
                        <p class="form-error" v-show="formErrors.HostName">{{formErrors.HostName}}</p>
                    </div>
                </div>
                <div class="flex-row">
                    <label class="flex-label" for="port">SMTP port <span class="red-star">*</span></label>
                    <div class="flex-input">
                        <input type="number" min="0" step="1" id="port" v-model="configuration.Port">
                        <p class="form-error" v-show="formErrors.Port">{{formErrors.Port}}</p>
                    </div>
                </div>
                <div class="flex-row">
                    <label class="flex-label" for="timeout">Timeout (s) <span class="red-star">*</span></label>
                    <div class="flex-input">
                        <input type="number" min="0" step="1" id="timeout" v-model="configuration.TimeoutInSeconds">
                        <p class="form-error" v-show="formErrors.TimeoutInSeconds">{{formErrors.TimeoutInSeconds}}</p>
                    </div>
                </div>
                <div class="flex-row">
                    <label class="flex-label" for="ssl">SSL</label>
                    <div class="flex-input">
                        <input type="checkbox" id="ssl" v-model="configuration.UseSSL">
                    </div>
                </div>
                <div class="flex-row">
                    <label class="flex-label" for="user-name">Username <span class="red-star">*</span></label>
                    <div class="flex-input">
                        <input type="text" id="user-name" v-model="configuration.UserName">
                        <p class="form-error" v-show="formErrors.UserName">{{formErrors.UserName}}</p>
                    </div>
                </div>
                <div class="flex-row">
                    <label class="flex-label" for="password">Password <span class="red-star">*</span></label>
                    <div class="flex-input">
                        <input type="password" id="password" v-model="configuration.Password">
                        <p class="form-error" v-show="formErrors.Password">{{formErrors.Password}}</p>
                    </div>
                </div>
                <div class="flex-row">
                    <div class="left-col"></div>
                    <div class="flex-input">
                        <label for="is-enabled">
                            <input type="checkbox" id="is-enabled" v-model="configuration.Enabled" />
                            Configuration enabled
                        </label>
                    </div>
                </div>
            </fieldset>
            <div class="splitter"></div>
            <div class="flex-row" v-show="waitingForResponse">
                <div class="left-col"></div>
                <div class="right-col spinning-wheel">
                    <img :src="imagePath + 'ajax-loader.gif'">
                    <span>{{loadingMessage}}</span>
                </div>
            </div>
            <fieldset v-if="isConfigurationFetched && !waitingForResponse" class="form-footer">
                <div class="flex-row">
                    <div class="left-col"></div>
                    <div class="right-col buttons">
                        <div class="col-button">
                            <input type="submit" value="Save">
                        </div>
                        <input type="button" value="Test" @click="test()" class="col-button">
                    </div>
                </div>
                <div class="flex-row">
                    <div class="left-col"></div>
                    <div class="right-col">
                        <p v-show="configuration.Id !== 0">
                            If you would like to remove this SMTP configuration, click <span class="inline-button" @click="removeConfiguration(configuration.Id)">here</span>.
                            </br> Keep in mind that you will need to fill out this form again to enable Mail Notifications.
                        </p>
                    </div>
                </div>
            </fieldset>
        </form>
    </div>
</template>
<script>
    import Vue from 'vue';
    import { mapGetters, mapActions } from 'vuex';
    import { NAMESPACE as SMTP_CONFIGURATION_NAMESPACE } from '../store/modules/smtpConfiguration.js';
    import { NAMESPACE as APP_NAMESPACE } from '../store/modules/app.js';
    import Notification from './Notification';

    const FIELD_IS_REQUIRED = 'This field is required';
    const KEYS_OF_REQUIRED_FIELDS = ['FromEmailAddress', 'ToEmailAddress', 'HostName',
        'UserName', 'Password', 'Port', 'TimeoutInSeconds'];

    export default {
        props: ['loadOnInit'],
        data() {
            return {
                mountCallback: function() {}, // its for unit tests purposes
                formErrors: {}
            }
        },
        computed: {
            ...mapGetters({
                configuration: SMTP_CONFIGURATION_NAMESPACE.getters.CONFIGURATION,
                waitingForResponse: SMTP_CONFIGURATION_NAMESPACE.getters.WAITING_FOR_RESPONSE,
                loadingMessage: SMTP_CONFIGURATION_NAMESPACE.getters.LOADING_MESSAGE,
                imagePath: APP_NAMESPACE.getters.IMAGE_PATH,
                viewNotification: SMTP_CONFIGURATION_NAMESPACE.getters.VIEW_NOTIFICATION
            }),
            isDisabled: function() {
                return !this.configuration.IsEnabled && this.configuration.Id;
            },
            isConfigurationFetched: function() {
            	return this.configuration.Id !== undefined;
            }
        },
        components: {
            Notification
        },
        methods: {
            ...mapActions({
                setUpService: SMTP_CONFIGURATION_NAMESPACE.actions.SET_UP_SERVICE,
                getConfiguration: SMTP_CONFIGURATION_NAMESPACE.actions.FETCH,
                saveConfiguration: SMTP_CONFIGURATION_NAMESPACE.actions.SAVE,
                testConfiguration: SMTP_CONFIGURATION_NAMESPACE.actions.TEST,
                removeConfiguration: SMTP_CONFIGURATION_NAMESPACE.actions.CONFIRM_REMOVE,
                disableConfiguration: SMTP_CONFIGURATION_NAMESPACE.actions.DISABLE,
                enableConfiguration: SMTP_CONFIGURATION_NAMESPACE.actions.ENABLE,
                registerListener: APP_NAMESPACE.actions.ADD_LISTENER
            }),
            ...mapGetters({
                contextPath: APP_NAMESPACE.getters.CONTEXT_PATH
            }),
            initComponent: async function() {
                await this.getConfiguration();
                this.mountCallback();
            },
            saveData: function(e) {
                e.preventDefault();
                this.formErrors = validateSmtpConfiguration(this.configuration);
                if (Object.keys(this.formErrors).length === 0) {
                    this.saveConfiguration(this.configuration);
                } else {
                    Vue.nextTick(scrollToFirstFormError);
                }
            },
            test: function() {
                this.formErrors = validateSmtpConfiguration(this.configuration);
                if (Object.keys(this.formErrors).length === 0) {
                    this.testConfiguration(this.configuration);
                } else {
                    Vue.nextTick(scrollToFirstFormError);
                }
            }
        },
        watch: {
            configuration: function(configuration) {
                if (configuration.SynchronizerUrl === null) {
                    configuration.SynchronizerUrl = getCurrentSynchronizerUrl(this.contextPath());
                }
            }
        },
        mounted: function() {
            this.setUpService({
                prefix: this.contextPath() + '/api/profiles'
            });
            if (this.loadOnInit === 'true') {
                this.initComponent();
            }
            this.registerListener({
                on: 'RefresSmtpConfigurationComponent',
                handler: function(notification) {
                    this.initComponent();
                }.bind(this)
            });
        }
    }

    function scrollToFirstFormError() {
        $('html, body').animate({
            scrollTop: parseInt($(".form-error:visible").offset().top - 100)
        }, 300);
    }

    function validateSmtpConfiguration(configuration) {
        let errors = {};
        KEYS_OF_REQUIRED_FIELDS.forEach((key) => {
            if (!configuration[key]) {
                errors[key] = FIELD_IS_REQUIRED;
            }
        });
        return Object.freeze(errors);
    }

    function getCurrentSynchronizerUrl(contextPath) {
        if (window.location.hostname.startsWith('localhost') || window.location.hostname.startsWith('127.0.0.')) {
            return '';
        }
        if (!window.location.origin) {
            return ''
        }
        return window.location.origin + contextPath;
    }
</script>



// WEBPACK FOOTER //
// src/components/SmtpConfiguration.vue